import { SectionHeader } from "./SectionHeader";
import type { Theme } from "./theme";

const FRICTIONS = [
  "roles are interpreted differently",
  "feedback is inconsistent",
  "decisions are influenced by opinion rather than evidence",
];

const ROWS = [
  { t: "a single role blueprint", s: "Roles defined once, shared everywhere." },
  { t: "shared competency definitions", s: "Everyone evaluates against the same bar." },
  { t: "consistent evaluation criteria", s: "Structured scorecards across rounds." },
  { t: "structured candidate evaluations", s: "Evidence against criteria, not opinion." },
];

const ENSURES = [
  "hiring managers and TA teams are aligned from the outset",
  "feedback is comparable across stakeholders",
  "decisions are based on evidence, not interpretation",
];

export function Collaboration({ theme }: { theme: Theme }) {
  return (
    <section
      style={{ padding: "clamp(72px, 12vw, 120px) 0", borderTop: `1px solid ${theme.border}` }}
    >
      <SectionHeader theme={theme} title="Aligned teams make better hiring decisions." />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 5vw, 40px)" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 64,
            marginBottom: 72,
          }}
        >
          <div>
            <p
              style={{
                fontSize: 19,
                color: theme.fgMute,
                lineHeight: 1.55,
                marginBottom: 20,
              }}
            >
              Hiring is not an individual activity. It is a shared decision across hiring managers,
              recruiters, and stakeholders.
            </p>
            <p
              style={{
                fontSize: 17,
                color: theme.fgMute,
                lineHeight: 1.6,
              }}
            >
              In most organisations, that collaboration is unstructured. This creates friction,
              delays, and inconsistent outcomes.
            </p>
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: theme.fgMute,
                marginBottom: 16,
                fontFamily: theme.mono,
              }}
            >
              Without alignment
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {FRICTIONS.map((f, i) => (
                <li
                  key={f}
                  style={{
                    padding: "16px 0",
                    borderTop: i === 0 ? `1px solid ${theme.border}` : "none",
                    borderBottom: `1px solid ${theme.border}`,
                    fontSize: 17,
                    color: theme.fg,
                    letterSpacing: -0.2,
                  }}
                >
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: theme.fgMute,
            marginBottom: 12,
            fontFamily: theme.mono,
          }}
        >
          A shared framework for hiring decisions
        </div>
        <p
          style={{
            fontSize: 17,
            color: theme.fgMute,
            lineHeight: 1.6,
            maxWidth: 760,
            marginBottom: 32,
          }}
        >
          Rarion creates alignment across everyone involved in the hiring process. Teams work from:
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 1,
            background: theme.border,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radius * 1.5,
            overflow: "hidden",
          }}
        >
          {ROWS.map((r, i) => (
            <div
              key={r.t}
              style={{
                padding: 28,
                minHeight: 220,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                background: theme.surface,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: theme.accentSoft,
                  color: theme.accentFg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: theme.mono,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                0{i + 1}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 500,
                    color: theme.fg,
                    letterSpacing: -0.2,
                    marginBottom: 8,
                  }}
                >
                  {r.t}
                </div>
                <div style={{ fontSize: 14, color: theme.fgMute, lineHeight: 1.5 }}>{r.s}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 40,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 64,
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: theme.fgMute,
                marginBottom: 16,
                fontFamily: theme.mono,
              }}
            >
              This ensures
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {ENSURES.map((e, i) => (
                <li
                  key={e}
                  style={{
                    padding: "14px 0",
                    borderTop: i === 0 ? `1px solid ${theme.border}` : "none",
                    borderBottom: `1px solid ${theme.border}`,
                    fontSize: 16,
                    color: theme.fg,
                    letterSpacing: -0.2,
                  }}
                >
                  {e}
                </li>
              ))}
            </ul>
          </div>
          <p
            style={{
              fontSize: 22,
              fontWeight: 500,
              color: theme.fg,
              letterSpacing: -0.3,
              lineHeight: 1.4,
              marginTop: 24,
            }}
          >
            Collaboration improves when it is{" "}
            <span style={{ color: theme.accentFg }}>structured</span>.
          </p>
        </div>
      </div>
    </section>
  );
}
